// react components
import React from 'react'
import {
    useSelector,
} from 'react-redux'

// data
import {
    contentTypeModelTypeSite,
    defaultReduxState,
} from 'data'

// pages
import {
    ErrorHelper825,
    FormFieldInput825,
    FormLabel825,
} from 'pages'

// serializers
import {
    ContentTypeFormBlockFieldSetTypes,
    ContentTypeFormTypes,
    ContentTypeSettingsTypes,
    ContentTypeSettingsCustomTypes,
} from 'serializers/site'

// services
import {
    getApiUrlSiteV2,
} from 'services'

// props
type FormFieldSet825Props = {
    blockId: number
    contentType: contentTypeModelTypeSite
    contentTypeSettings: ContentTypeSettingsTypes
    contentTypeSettingsCustom: ContentTypeSettingsCustomTypes | undefined
    errors: ContentTypeFormTypes['data']['errors']
    fieldSetObject: ContentTypeFormBlockFieldSetTypes
    getMetaIsLoading?: boolean
    object: ContentTypeFormTypes['data']['fields']
    onChange: any
    onKeyPress: any
    subContentTypeForced?: { id: number, name: string }
    verticalStyle?: boolean
}

// main
export const FormFieldSet825: React.FC<FormFieldSet825Props> = React.memo(({
    blockId,
    contentType,
    contentTypeSettings,
    contentTypeSettingsCustom,
    errors,
    fieldSetObject,
    getMetaIsLoading,
    object,
    onChange,
    onKeyPress,
    subContentTypeForced,
    verticalStyle,
}) => {

    const reduxModalSite = useSelector((state: defaultReduxState) => state.reduxModalSite)

    let fieldSetObjectFields = fieldSetObject.fields

    if ([
        'extra_custom',
        'extra_main',
        'meta_helper',
        // 'meta_image',
        'meta_json',
        'meta_privacy',
        'meta_rel',
    ].includes(fieldSetObject.blockTypeOther)) {
        fieldSetObjectFields = fieldSetObjectFields.sort((a, b) => {
            const order = [
                'sub_content_type',
                'module_views',
                'module_view',
            ]

            // Check if either value is in the predefined order
            const indexA = order.indexOf(a)
            const indexB = order.indexOf(b)

            // If both are in the order, sort based on their position in the order array
            if (indexA !== -1 && indexB !== -1) {
                return indexA - indexB
            }

            // If only one is in the order, prioritize that one
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1

            // Otherwise, sort alphabetically
            return a.localeCompare(b)
        })
    }

    const app = contentType.split('_')[0]
    const model = contentType.split('_')[1]

    const translationFormInputDict: any = {
        CharField: 'text',
        ImageField: 'image',
        JSONField: 'address_json',
        RichTextField: 'richText',
        TextField: 'textArea',
        URLField: 'text',
        // image_crop: 'image_crop',  // TODO
        // pdf: 'pdf',  // TODO
    }

    return (
        <div className={`form-component-field-set-825 ${blockId}`}>
            <div className='fcfs-fieldsets'>
                {fieldSetObject.title && (
                    <div className='fcfs-wrap-title'>
                        <p className='fcfs-title'>{fieldSetObject.title}</p>
                    </div>
                )}
                <div className='fcfs-fields'>
                    {fieldSetObjectFields?.map((val) => {
                        const fieldModelFieldSettings = contentTypeSettings.field?.[val]
                        let fieldsOptionsSettings = undefined
                        fieldsOptionsSettings = {
                            ...contentTypeSettings.form.fieldsOptions?.[val],
                            ...contentTypeSettingsCustom?.form?.fieldsOptions?.[val],
                            ...contentTypeSettingsCustom?.form?.fieldsOptionsSubContentType?.[subContentTypeForced?.id || object.sub_content_type?.id]?.[val],
                        }
                        fieldsOptionsSettings.selectUrlParams = {
                            ...contentTypeSettings.form.fieldsOptions?.[val]?.selectUrlParams,
                            ...contentTypeSettingsCustom?.form?.fieldsOptions?.[val]?.selectUrlParams,
                            ...contentTypeSettingsCustom?.form?.fieldsOptionsSubContentType?.[subContentTypeForced?.id || object.sub_content_type?.id]?.[val]?.selectUrlParams,
                        }
                        if (fieldsOptionsSettings.selectChoices?.length! > 0) {
                            fieldModelFieldSettings.fieldType = 'ChoiceField'
                        }
                        if (!fieldModelFieldSettings) return (
                            <p className='mo-error-color'>Error: Missing fieldModelFieldSettings for {val}</p>
                        )
                        const fieldIsDisabled = (fieldsOptionsSettings?.disableIfNotStaff)
                            || (fieldsOptionsSettings?.disableIfNotSuperuser)
                            || (fieldsOptionsSettings?.disableIfId && object.id)
                            || fieldsOptionsSettings?.disabled
                        if (fieldsOptionsSettings?.hideIfNotStaff) return null
                        if (fieldsOptionsSettings?.hideIfNotSuperuser) return null
                        // Handle isHiddenIf condition
                        if (fieldsOptionsSettings?.isHiddenIf) {
                            const condition = fieldsOptionsSettings.isHiddenIf
                            const conditionParts = condition.split('=')
                            if (conditionParts.length === 2) {
                                const [conditionFieldName, conditionValue] = conditionParts
                                let conditionFieldValue = object[conditionFieldName]

                                // If condition field is not found, try to find similar field names
                                if (conditionFieldValue === undefined) {
                                    const availableFields = Object.keys(object)
                                    const similarFields = availableFields.filter(field =>
                                        field.includes(conditionFieldName) || conditionFieldName.includes(field)
                                    )

                                    // Try the first similar field if found
                                    if (similarFields.length > 0) {
                                        conditionFieldValue = object[similarFields[0]]
                                    }
                                }

                                // Check if the condition field has a value
                                if (conditionFieldValue !== undefined && conditionFieldValue !== null) {
                                    // Extract the actual value for comparison
                                    let actualValue = conditionFieldValue

                                    // Check if it's an array first
                                    if (Array.isArray(conditionFieldValue)) {
                                        // Handle array case - check if any item in the array matches the condition value
                                        const matchingItems = conditionFieldValue.filter(item => {
                                            let itemValue = item
                                            if (typeof item === 'object' && item !== null) {
                                                itemValue = item.id || item.value || item
                                            }
                                            return String(itemValue) === String(conditionValue)
                                        })

                                        if (matchingItems.length > 0) {
                                            actualValue = conditionValue // Use the condition value since we found a match
                                        } else {
                                            // Fallback to first item for other logic
                                            const firstItem = conditionFieldValue[0]
                                            if (typeof firstItem === 'object' && firstItem !== null) {
                                                actualValue = firstItem.id || firstItem.value || firstItem
                                            } else {
                                                actualValue = firstItem
                                            }
                                        }
                                    } else if (typeof conditionFieldValue === 'object' && conditionFieldValue !== null) {
                                        // Handle single object case
                                        if (conditionFieldValue.id !== undefined) {
                                            actualValue = conditionFieldValue.id
                                        } else if (conditionFieldValue.value !== undefined) {
                                            actualValue = conditionFieldValue.value
                                        }
                                    }

                                    let shouldHide = false

                                    // Handle special case for 'true' value
                                    if (conditionValue === 'true' && actualValue) {
                                        shouldHide = true
                                    } else {
                                        // Handle negation case (not__value)
                                        const valueParts = conditionValue.split('__')
                                        if (valueParts.length === 2 && valueParts[0] === 'not') {
                                            if (String(actualValue) !== String(valueParts[1])) {
                                                shouldHide = true
                                            }
                                        } else {
                                            // Handle normal equality case
                                            if (String(actualValue) === String(conditionValue)) {
                                                shouldHide = true
                                            }
                                        }
                                    }

                                    if (shouldHide) return null
                                } else {
                                    // Condition field is empty/undefined - check isHiddenIfDefault
                                    if (fieldsOptionsSettings?.isHiddenIfDefault) {
                                        const fieldValue = object[val]
                                        let isDefaultValue = false

                                        // Check if field has a default/empty value
                                        if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                                            isDefaultValue = true
                                        } else if (Array.isArray(fieldValue)) {
                                            isDefaultValue = fieldValue.length === 0
                                        } else if (typeof fieldValue === 'object') {
                                            isDefaultValue = Object.keys(fieldValue).length === 0
                                        } else if (typeof fieldValue === 'number') {
                                            isDefaultValue = fieldValue === 0
                                        } else if (typeof fieldValue === 'boolean') {
                                            isDefaultValue = fieldValue === false
                                        }

                                        if (isDefaultValue) return null
                                    }
                                }
                            }
                        } else if (fieldsOptionsSettings?.isHiddenIfDefault) {
                            // Only apply isHiddenIfDefault when there's no isHiddenIf condition
                            const fieldValue = object[val]
                            let isDefaultValue = false

                            // Check if field has a default/empty value
                            if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
                                isDefaultValue = true
                            } else if (Array.isArray(fieldValue)) {
                                isDefaultValue = fieldValue.length === 0
                            } else if (typeof fieldValue === 'object') {
                                isDefaultValue = Object.keys(fieldValue).length === 0
                            } else if (typeof fieldValue === 'number') {
                                isDefaultValue = fieldValue === 0
                            } else if (typeof fieldValue === 'boolean') {
                                isDefaultValue = fieldValue === false
                            }

                            if (isDefaultValue) return null
                        }
                        const name = contentTypeSettingsCustom?.fieldSubContentType?.[subContentTypeForced?.id || object.sub_content_type?.id]?.[val]?.verboseName || contentTypeSettingsCustom?.field?.[val]?.verboseName || contentTypeSettings.field?.[val]?.verboseName!
                        const label = name || val
                        let translationFormInput = undefined
                        let translationDefaultValue = undefined
                        if (object.portfolio_is_multi_language) {
                            if (val === 'pdf_file') {
                                translationFormInput = 'pdf'
                                translationDefaultValue = object[val]
                            } else if (object[`${val}_is_rich`]) {
                                translationFormInput = 'richText'
                                translationDefaultValue = object[`${val}_rich`]
                            } else if (typeof object[`${val}_original`] !== 'undefined') {
                                translationFormInput = 'image_crop'
                                translationDefaultValue = object[val]
                            } else {
                                translationFormInput = translationFormInputDict[fieldModelFieldSettings.fieldType]
                                translationDefaultValue = object[val]
                            }
                        }
                        return (
                            <div
                                key={val}
                                className={`fcfs-input${fieldIsDisabled ? ' disabled' : ''}`}
                                style={verticalStyle ? { flexDirection: 'column' } : {}}
                            >
                                <div
                                    className='fcfs-label-wrap'
                                    style={verticalStyle ? { marginBottom: '10px', width: 'auto' } : {}}
                                >
                                    <FormLabel825
                                        formInput={translationFormInput}
                                        helperText={fieldsOptionsSettings?.helperText}
                                        helperTextNoTooltip
                                        label={label}
                                        name={val}
                                        object={object}
                                        translationDefaultValue={translationDefaultValue}
                                        translation={translationFormInput ? {
                                            contentType,
                                            defaultDetailUrl: getApiUrlSiteV2(`${app}/form/${model}/detail/`, reduxModalSite),
                                            defaultLanguage: object.translation_default_language,
                                            defaultUpdateUrl: getApiUrlSiteV2(`${app}/form/${model}/update/`, reduxModalSite),
                                            objectId: object.id,
                                            profileId: object.profile?.id,
                                        } : undefined}
                                    />
                                </div>
                                <div className='fcfs-input-wrap'>
                                    <FormFieldInput825
                                        contentType={contentType}
                                        contentTypeSettings={contentTypeSettings}
                                        contentTypeSettingsCustom={contentTypeSettingsCustom}
                                        disabled={fieldIsDisabled}
                                        errors={errors}
                                        field={val}
                                        fieldType={fieldModelFieldSettings.fieldType}
                                        fieldsOptionsSettings={fieldsOptionsSettings}
                                        getMetaIsLoading={getMetaIsLoading}
                                        label={label}
                                        object={object}
                                        onChange={onChange}
                                        onKeyPress={onKeyPress}
                                    />
                                    <ErrorHelper825 error={errors?.[val]} />
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>
        </div>
    )
})
